import Utility from 'classes/Utility';
import FormController from './form_controller';
import axios from 'axios';

class EmailSubscribeForm extends FormController {
  static targets = [
    'email',
    'advisorType',
    'errorMessage',
    'emailError',
    'advisorTypeError',
    'form',
    'confirmationMessage',
    'submitButton'
  ]

  static values = {
    componentType: String
  }

  targetNames() {
    return EmailSubscribeForm.targets;
  }

  validate() {
    // Clear all errors first
    this.clearAllErrors();

    let isValid = true;

    // Validate email
    if (!this.emailTarget.value || !this.validateEmail(this.emailTarget.value)) {
      this.emailTarget.classList.add('t-border-red-600', 't-border');
      // Use specific error element if it exists, otherwise fall back to general error
      if (this.hasEmailErrorTarget) {
        this.emailErrorTarget.textContent = "Please enter a valid email address";
        this.emailErrorTarget.classList.remove('t-hidden');
      } else if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.textContent = "Please enter a valid email address";
        this.errorMessageTarget.classList.remove('t-hidden');
      }
      isValid = false;
    }

    // Validate investor type - check if a real option is selected (not just placeholder)
    const selectedIndex = this.advisorTypeTarget.selectedIndex;
    const selectedOption = this.advisorTypeTarget.options[selectedIndex];
    const isPlaceholder = selectedOption.disabled || selectedIndex === 0;

    if (!this.advisorTypeTarget.value || isPlaceholder) {
      this.advisorTypeTarget.classList.add('t-border-red-600', 't-border');
      // Use specific error element if it exists, otherwise fall back to general error
      if (this.hasAdvisorTypeErrorTarget) {
        this.advisorTypeErrorTarget.textContent = "Please select an investor type";
        this.advisorTypeErrorTarget.classList.remove('t-hidden');
      } else if (this.hasErrorMessageTarget && isValid) {
        // Only update general error if there's no email error
        this.errorMessageTarget.textContent = "Please select an investor type";
        this.errorMessageTarget.classList.remove('t-hidden');
      }
      isValid = false;
    }

    return isValid;
  }

  validateEmail(email) {
    const regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
  }

  async subscribe() {
    try {
      // Validate form first
      if (!this.validate()) {
        return;
      }

      // Disable button to prevent multiple submissions
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true;
      }

      // Get current URL without domain (cross-browser compatible)
      const fullUrl = window.location.href;
      const origin = window.location.origin ||
                    (window.location.protocol + "//" + window.location.hostname +
                    (window.location.port ? ':' + window.location.port : ''));
      const currentUrl = fullUrl.substring(origin.length);

      // Set status based on component type
      const status = this.componentTypeValue === 'article' ? 'pending' : 'subscribed';

      await axios.post('/api/newsletter/', {
        email: this.emailTarget.value,
        investor_type: this.advisorTypeTarget.value,
        signup_location: currentUrl,
        status: status
      });

      // Handle success - show confirmation if element exists
      if (this.hasFormTarget && this.hasConfirmationMessageTarget) {
        this.formTarget.classList.add('t-hidden');
        this.confirmationMessageTarget.classList.remove('t-hidden');
      }

      this.showMessage('SUCCESS', 'Thank you', 'Please check your inbox to verify your email address.');
    } catch (err) {
      this.showMessage('ERROR', 'Something went wrong','Sorry, you are not subscribed successfully. Please try again.');
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
      }
    }
  }

  showMessage(status, title, message) {
    EventManager.emit('flash-message', { status: status, title: title, message: message });
  }

  showError() {
    this.errorMessageTarget.textContent = "Looks like we're missing some information.";
    this.errorMessageTarget.classList.remove('t-hidden');
  }

  selectAdvisorType() {
    let investor_value = this.advisorTypeTarget.value;
    Utility.setLocalCookies('invstr_seg', investor_value, 'iSe', true);
  }

  connect() {
    super.connect();

    // Set default value for componentType if not set
    if (!this.hasComponentTypeValue) {
      this.componentTypeValue = 'article';
    }

    // Auto-refresh if newsletter_confirmed cookie is present and we haven't just refreshed
    if (document.cookie.includes('newsletter_confirmed=true') && !sessionStorage.getItem('newsletter_page_refreshed')) {
      // Set a session storage flag to prevent reload loops
      sessionStorage.setItem('newsletter_page_refreshed', 'true');
      window.location.reload();
    }

    // Add input event handlers to clear errors when typing
    if (this.hasEmailTarget) {
      this.emailTarget.addEventListener('input', () => this.clearEmailError());
    }

    if (this.hasAdvisorTypeTarget) {
      this.advisorTypeTarget.addEventListener('change', () => this.clearAdvisorTypeError());
    }
  }

  clearEmailError() {
    this.emailTarget.classList.remove('t-border-red-600', 't-border');
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.classList.add('t-hidden');
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('t-hidden');
    }
  }

  clearAdvisorTypeError() {
    this.advisorTypeTarget.classList.remove('t-border-red-600', 't-border');
    if (this.hasAdvisorTypeErrorTarget) {
      this.advisorTypeErrorTarget.classList.add('t-hidden');
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('t-hidden');
    }
  }

  clearAllErrors() {
    this.clearEmailError();
    this.clearAdvisorTypeError();
  }

  submit(event) {
    event.preventDefault();
    this.subscribe();
  }
}

export default EmailSubscribeForm;
